import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin } from 'lucide-react';
import { formatWhatsAppLink, formatPhoneNumber, PHONE_NUMBER, WHATSAPP_NUMBER } from '../utils/whatsapp';
import { useSiteData } from '../contexts/SiteDataContext';
import { companyInfo } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { settings, categories, pageContents } = useSiteData();
  const footerData = pageContents.footer || {};
  const footerMeta = footerData.metadata || {};
  const email = footerMeta.email || settings.email || companyInfo.email;
  const address = footerMeta.address || settings.address || companyInfo.address;
  const social = settings.social || companyInfo.social;
  const slogan = settings.siteSlogan || companyInfo.slogan;
  const footerDesc = footerData.content || 'Especialistas em componentes hidráulicos agrícolas e industriais.';
  const logoUrl = 'https://customer-assets.emergentagent.com/job_hydro-specialist/artifacts/1se2hklr_Verde%20e%20Branco%20Startup%20de%20Soluc%CC%A7o%CC%83es%20Agrono%CC%82micas%20Moderno%20Carta%CC%83o%20de%20Visita%20-%201.jpeg';
  const logoFallback = 'https://customer-assets.emergentagent.com/job_5793c14a-9e5d-45df-be07-4fd47ebae7d7/artifacts/63qxnlv7_16.png';
  const topCategories = categories.slice(0, 5);

  return (
    <footer className="bg-[#055509] text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <img 
              src={logoUrl} 
              alt="TerraHydro" 
              className="h-12 w-auto mb-4 bg-white rounded p-1"
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = logoFallback; }}
            />
            <p className="text-sm text-gray-300 mb-4">
              {slogan}
            </p>
            <p className="text-sm text-gray-300">
              {footerDesc}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-[#FFB025] font-semibold mb-4 text-lg">Links Rápidos</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm text-gray-300 hover:text-[#FFB025] transition-colors">
                  Início
                </Link>
              </li>
              <li>
                <Link to="/produtos" className="text-sm text-gray-300 hover:text-[#FFB025] transition-colors">
                  Produtos
                </Link>
              </li>
              <li>
                <Link to="/categorias" className="text-sm text-gray-300 hover:text-[#FFB025] transition-colors">
                  Categorias
                </Link>
              </li>
              <li>
                <Link to="/sobre" className="text-sm text-gray-300 hover:text-[#FFB025] transition-colors">
                  Sobre Nós
                </Link>
              </li>
              <li>
                <Link to="/contato" className="text-sm text-gray-300 hover:text-[#FFB025] transition-colors">
                  Contato
                </Link>
              </li>
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h3 className="text-[#FFB025] font-semibold mb-4 text-lg">Categorias</h3>
            <ul className="space-y-2">
              {topCategories.map(cat => (
                <li key={cat.id || cat.slug}>
                  <Link to={`/categorias/${cat.slug}`} className="text-sm text-gray-300 hover:text-[#FFB025] transition-colors">
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-[#FFB025] font-semibold mb-4 text-lg">Contato</h3>
            <ul className="space-y-3">
              <li>
                <a 
                  href={formatWhatsAppLink()} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-start gap-2 text-sm text-gray-300 hover:text-[#FFB025] transition-colors"
                >
                  <Phone size={16} className="mt-0.5 flex-shrink-0" />
                  <span>WhatsApp: {formatPhoneNumber(WHATSAPP_NUMBER)}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`tel:${PHONE_NUMBER}`}
                  className="flex items-start gap-2 text-sm text-gray-300 hover:text-[#FFB025] transition-colors"
                >
                  <Phone size={16} className="mt-0.5 flex-shrink-0" />
                  <span>Fixo: {formatPhoneNumber(PHONE_NUMBER)}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${email}`}
                  className="flex items-start gap-2 text-sm text-gray-300 hover:text-[#FFB025] transition-colors"
                >
                  <Mail size={16} className="mt-0.5 flex-shrink-0" />
                  <span>{email}</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-2 text-sm text-gray-300">
                  <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                  <span>
                    {address.street}<br />
                    {address.city} - {address.state}
                  </span>
                </div>
              </li>
            </ul>

            {/* Social Media */}
            <div className="flex items-center gap-3 mt-4">
              <a 
                href={`https://instagram.com/${(social.instagram || '').replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center bg-white bg-opacity-10 rounded-full hover:bg-[#FFB025] transition-colors"
              >
                <Instagram size={16} />
              </a>
              <a 
                href={`https://facebook.com/${social.facebook || ''}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center bg-white bg-opacity-10 rounded-full hover:bg-[#FFB025] transition-colors"
              >
                <Facebook size={16} />
              </a>
              <a 
                href={`https://linkedin.com/${social.linkedin || ''}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center bg-white bg-opacity-10 rounded-full hover:bg-[#FFB025] transition-colors"
              >
                <Linkedin size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white border-opacity-10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-300">
            <p>
              © {currentYear} TerraHydro Soluções Hidráulicas. Todos os direitos reservados.
            </p>
            <div className="flex gap-6">
              <Link to="/politica-privacidade" className="hover:text-[#FFB025] transition-colors">
                Política de Privacidade
              </Link>
              <Link to="/termos-uso" className="hover:text-[#FFB025] transition-colors">
                Termos de Uso
              </Link>
              <Link to="/dashboard/login" className="hover:text-[#FFB025] transition-colors opacity-50 hover:opacity-100">
                Admin
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
